<script lang="ts">
    import { onDestroy } from 'svelte'
    import { dndzone } from 'svelte-dnd-action'
    import type { Readable } from 'svelte/store'

    import { orderBy } from 'lodash-es'

    import type { Crumbs } from 'hydria/components/breadcrumb'
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'

    import type { IAddress } from 'wdom/services/contacts'
    import type { User } from 'admin/routes/Users/User/user'
    import { Breadcrumb } from 'breadcrumb'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import Country from 'wdom/components/contacts/Country.svelte'
    import PostalCode from 'wdom/components/contacts/PostalCode.svelte'
    import Province from 'wdom/components/contacts/Province.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        // export let loading: boolean
        user: Readable<User>
        crumbs: Crumbs
        mode?: 'single' | 'batch'
        saved?: () => void
    }

    let { user, crumbs, mode = 'single', saved }: Props = $props()

    const wStorage = window.sessionStorage

    let metaLoading = $state(true)
    // Order as desplayed / changed by the user
    let eMetaOrder: string[]

    let address = $derived($user != null ? ($user.metadata as IAddress) : null)
    let saving = $state(false)

    function loadLocalOrder() {
        let storedOrder: string[]
        try {
            // @ts-expect-error
            storedOrder = JSON.parse(wStorage.getItem('emeta-local-order'))
            eMetaOrder = storedOrder ? storedOrder : []
        } catch (e) {
            eMetaOrder = []
        }
    }
    loadLocalOrder()

    function storeLocalOrder() {
        wStorage.setItem('emeta-local-order', JSON.stringify(eMetaOrder, null, 0))
    }

    interface EMetaFieldChoice {
        type: 'choice'
        tag: string
        name: string
        choices: {
            tag: string
            text: string
        }[]
    }
    interface EMetaFieldString {
        type: 'string'
        tag: string
        name: string
    }

    // Metadata configuration (from the backend)
    interface EMetaConfig {
        // id: string
        fields: (EMetaFieldChoice | EMetaFieldString)[]
        field_order: string[]
    }
    let eMetaConfig: EMetaConfig

    // Values (from the backend)
    interface EMetaValues {
        [key: string]: string | null
    }
    let eMetaValues: EMetaValues = $state()

    // Field order as sent by the backend
    let eMetaOriginalOrder: string[]

    // A sorted copy of eMetaConfig.fields based on eMetaOrder
    // id === tag, for 'svelte-dnd-action' that *requires* something called 'id'
    let eMetaOrderedFields: { id: number; tag: string; field: EMetaFieldChoice | EMetaFieldString }[] = $state()
    let eMetaIdCount = 0

    // Field index (indexed version of eMetaConfig.fields)
    let eMetaFieldIndex: { [key: string]: EMetaFieldChoice | EMetaFieldString } = $state()

    function generateOrderedFields() {
        eMetaOrderedFields = orderBy(
            eMetaConfig.fields.map((field) => {
                return {
                    id: eMetaIdCount++,
                    tag: field.tag,
                    field: field,
                }
            }),
            [(d) => eMetaOrder.indexOf(d.tag)],
            ['asc'],
        )
    }
    function orderFromOrdered() {
        eMetaOrder = eMetaOrderedFields.map((o) => o.tag)
        storeLocalOrder()
    }

    async function loadMeta(userId: string) {
        let r: any
        metaLoading = true
        try {
            r = await simpleFetch('GET', `/json/wdom-user/1/${userId}/extra-meta`)
        } catch (e) {
            console.error('Failed to load metadata:', e)
            notify({
                type: 'danger',
                title: 'Métadonnées',
                text: 'Une erreur est survenue lors du chargement de l’information.',
            })
            return
        }

        eMetaConfig = {
            fields: r.data.fields,
            field_order: r.data.field_order,
        }
        eMetaValues = r.data.data ? r.data.data : {}
        eMetaOriginalOrder = [...r.data.field_order]

        eMetaFieldIndex = {}
        for (const field of eMetaConfig.fields) {
            eMetaFieldIndex[field.tag] = field
        }

        for (const f of eMetaConfig.fields) {
            if (!eMetaValues[f.tag]) eMetaValues[f.tag] = null
        }

        // Add missing fields to eMetaOrder
        const lOrder = eMetaOrder
        for (const o of eMetaOriginalOrder) {
            if (!lOrder.includes(o)) eMetaOrder.push(o)
        }
        // Remove extra fields from eMetaOrder
        eMetaOrder = lOrder.filter((o) => eMetaOriginalOrder.includes(o))
        generateOrderedFields()

        metaLoading = false
    }
    unsubs.push(
        user.subscribe(($user) => {
            if ($user) loadMeta($user.id)
        }),
    )

    function handleSort(e) {
        eMetaOrderedFields = e.detail.items
    }
    function handleSortEnd(e) {
        eMetaOrderedFields = e.detail.items
        orderFromOrdered()
    }

    async function save(andGoBack: boolean = false) {
        let r: any

        saving = true

        try {
            r = await simpleFetch('PUT', `/json/wdom-user/1/${$user.id}/extra-meta`, { fields_data: { ...eMetaValues } })
        } catch (e) {
            console.error('Failed to save extra meta:', e)
            notify({
                type: 'danger',
                title: 'Métadonnées',
                text: 'Erreur de sauvegarde.',
            })
            saving = false
            return
        }

        notify({
            type: 'success',
            title: 'Métadonnées',
            text: 'Données sauvegardées.',
        })
        if (andGoBack) {
            sgoto(`/wdom-user/${$user.id}`)
        } else {
            saving = false
            if (saved) saved()
        }
    }
</script>

<div class="emeta-entry-layout">
    <div class="emeta-entry-main-group">
        <Breadcrumb {crumbs} />

        <div class="emeta-entry-form">
            <div class="card">
                <div class="card-body">
                    {#if metaLoading}
                        <div class="user-extra-meta-field uem-type-choice">
                            <div class="uem-name"><LoadingPattern pattern={[30 + Math.random() * 20]} /></div>
                            <div class="uem-answer">
                                {#each Array(10) as n}
                                    <LoadingPattern pattern={[40 + Math.random() * 20]} />
                                {/each}
                            </div>
                        </div>
                    {:else}
                        {#each eMetaOrderedFields as ofield}
                            <div class="user-extra-meta-field uem-type-{ofield.field.type} uem-tag-{ofield.field.tag}">
                                <div class="uem-name">{ofield.field.name}</div>
                                {#if ofield.field.type == 'choice'}
                                    <div class="uem-answer">
                                        {#each ofield.field.choices as choice, i}
                                            <div class="radio">
                                                <label for="rchoice-{ofield.field.tag}-{i}">
                                                    <input type="radio" id="rchoice-{ofield.field.tag}-{i}" bind:group={eMetaValues[ofield.field.tag]} value={choice.tag} disabled={saving} />
                                                    {choice.text}
                                                </label>
                                            </div>
                                        {/each}
                                    </div>
                                {:else if ofield.field.type == 'string'}
                                    <input type="text" class="form-control" bind:value={eMetaValues[ofield.field.tag]} disabled={saving} />
                                {/if}
                            </div>
                        {/each}
                    {/if}
                </div>
            </div>
        </div>

        <div class="emeta-entry-sidebar gap-4">
            <div class="card" id="emeta-sb-actions">
                <div class="card-title text-primary">Actions</div>
                <div class="card-body">
                    <div class="action-grid">
                        {#if mode == 'batch'}
                            <button type="button" class="btn btn-primary" id="ag-save-next" onclick={() => save(false)} disabled={!$user || metaLoading || saving}>Sauvegarder et suivant</button>
                        {:else}
                            {#if $user}
                                <a class="btn btn-sm btn-primary" id="ag-back" use:sanchor href={spath(`/wdom-user/${$user.id}`)}>Retour</a>
                            {/if}
                            <button type="button" class="btn btn-sm btn-primary" id="ag-save" onclick={() => save(false)} disabled={!$user || metaLoading || saving}>Sauvegarder</button>
                            <button type="button" class="btn btn-primary" id="ag-save-back" onclick={() => save(true)} disabled={!$user || metaLoading || saving}>Sauvegarder et retour</button>
                        {/if}
                    </div>
                </div>
            </div>

            <div class="card" id="emeta-sb-user">
                <div class="card-title text-primary">Utilisateur</div>
                <div class="card-body">
                    <dl>
                        <dt>Nom complet</dt>
                        {#if address}
                            <dd>{address.name ? address.name : '<sans nom>'}</dd>
                        {:else}
                            <dd><LoadingPattern pattern={[50]} /></dd>
                        {/if}
                        {#if !address || address.org}
                            <dt>Organisation</dt>
                            {#if address}
                                <dd>{address.org}</dd>
                            {:else}
                                <dd><LoadingPattern pattern={[37]} /></dd>
                            {/if}
                        {/if}

                        <dt>Adresse</dt>
                        {#if address}
                            <dd>
                                {#if address.street && address.street.length}
                                    {#each address.street as street, i}
                                        {#if i}<br />{/if}{street}
                                    {/each}
                                {:else}
                                    &lt;sans rue&gt;
                                {/if}
                                {#if address.city || address.sp}
                                    <br />
                                {/if}
                                {#if address.city && address.sp}
                                    {address.city}, <Province contact={address} />
                                {:else if address.city}
                                    {address.city}
                                {:else if address.sp}
                                    <Province contact={address} />
                                {/if}
                                {#if address.country || address.postal_code}
                                    <br />
                                    {#if address.country}<Country contact={address} />{/if}{#if address.country && address.postal_code},
                                    {/if}{#if address.postal_code}<PostalCode contact={address} />{/if}
                                {/if}
                            </dd>
                        {:else}
                            <dd><LoadingPattern pattern={[52, 45, 37]} /></dd>
                        {/if}
                    </dl>
                </div>
            </div>

            <div class="card" id="emeta-sb-order">
                <div class="card-title text-primary">Ordre</div>
                <div class="card-body">
                    {#if !metaLoading}
                        <ol class="emeta-order-list list-unstyled" use:dndzone={{ items: eMetaOrderedFields }} onconsider={handleSort} onfinalize={handleSortEnd}>
                            {#each eMetaOrderedFields as ofield (ofield.id)}
                                <li class="angular-ui-tree-node"><div class="angular-ui-tree-handle">{eMetaFieldIndex[ofield.tag].name}</div></li>
                            {/each}
                        </ol>
                    {:else}
                        <LoadingPattern pattern={['x', 'x', 'x']} />
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>
